import React, {useEffect, useState} from 'react'
import Tr from 'translations/Translations';
import Dialog from 'components/Dialog';
import DoubleButton from 'components/popup/components/DoubleButton';
import useId from 'hooks/useId';
import RegistrationService from "services/RegistrationService";
import Loading from "components/Loading";
import VS from "constants/ValidationShema";
import {connect} from "react-redux";
import TextInput from "components/TextInput";
import GridBlock from "components/GridBlock";
import Form from "components/Form";
import Button from "components/Button";
import PlusOneIcon from "@material-ui/icons/PlusOne";
import {FieldArray} from "formik";
import Select, {createSelectItem} from "components/Select";
import getObjectValue from "libs/getObjectValue";
import BoldTypography from "components/BoldTypography";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import {FormHelperText, makeStyles} from "@material-ui/core";
import isResponseValid from "../../../../../libs/isResponseValid";
import useSnackbar from "../../../../../hooks/useSnackbar";

const useStyle = makeStyles(theme => ({
    representativeGrid: {
        "&>*:nth-child(1)": {
            width: 160,
            marginRight: theme.spacing(3)
        },
        "&>*:nth-child(2)": {
            flexGrow: 1,
            marginRight: theme.spacing(3)
        },
        "&>*:nth-child(3)": {
            width: 350,
            marginRight: theme.spacing(3)
        },
        "&>*:nth-child(4)": {
            width: 25,
            marginRight: theme.spacing(3)
        },
    }
}))


const EMPTY_REPRESENTATIVE = {identity: 0, title: "", name: "", role: ""}

function UserRegistrationAddOrganizationUnit(
    {
        registrationId,
        onCancel,
        onCreate,
        formSubmit
    }) {

    const formId = useId();
    const handleSubmit = () => formSubmit[formId]()
    const [wrapper, setWrapper] = useState(null);
    const {enqueueErrorSnackbar} = useSnackbar();

    useEffect(() => {
        RegistrationService.getRegistrationOrganization(registrationId).then(setWrapper);
        // eslint-disable-next-line
    }, [])

    const addRepresentative = (arrayHelpers) => {
        arrayHelpers.push(EMPTY_REPRESENTATIVE);
    }

    const removeRepresentative = (arrayHelpers, index) => {
        arrayHelpers.remove(index);
    }

    const onSubmit = (values) => {
        RegistrationService.saveRegistrationOrganization(registrationId, values).then((resp) => {
            if(isResponseValid(resp, enqueueErrorSnackbar)) {
                onCreate(resp)
            }
        }).catch(()=> enqueueErrorSnackbar(Tr("unknown_error_message")));
    }

    return (
        <Dialog
            title={Tr('add_organization_unit')}
            open
            maxWidth="lg"
            dialogActions={
                <DoubleButton
                    justify='flex-end'
                    labelFirst={Tr('cancel')}
                    labelSecond={Tr('add_organization_unit')}
                    onClickFirst={onCancel}
                    onClickSecond={handleSubmit}
                    disabledSecond={!wrapper}
                />}
        >
            {
                !wrapper ? <Loading/> :
                    <Form
                        initialValues={{
                            ...wrapper.organization,
                            fieldsCount: 1
                        }}
                        validationSchema={VS.lazy((values) => {

                            return VS.object({
                                name: VS.stringMax(200),
                                externalId: VS.stringMax(10),
                                city: VS.stringMax(50),
                                address: VS.stringMax(200),
                                postCode: VS.stringMax(6),
                                phone: VS.phoneOptional(),
                                fax: VS.phoneOptional(),
                                email: VS.emailMaxNoRq(255),
                                nip: VS.vatinPLOptional(),
                                regon: VS.stringMaxNoRq(14),
                                representatives: VS.arrayOfShape({
                                    title: VS.string(),
                                    name: VS.stringMax(250),
                                    role: VS.string()
                                }),
                                fieldsCount: values.representatives.length === 0 ? VS.immediatelyError(Tr("required_field")) : VS.any()
                            })
                        })}
                        onSubmit={onSubmit}
                        id={formId}
                    >
                        {(formikProps) => (
                            <GridBlock column>
                                <TextInput
                                    id='name'
                                    formikProps={formikProps}
                                    label={Tr('organization_name')}
                                    limit={200}
                                    required
                                />
                                <TextInput
                                    id='externalId'
                                    formikProps={formikProps}
                                    label={Tr('organization_externalId')}
                                    limit={10}
                                    required
                                />
                                <TextInput
                                    id='city'
                                    formikProps={formikProps}
                                    label={Tr('city')}
                                    limit={50}
                                    required
                                />
                                <TextInput
                                    id='address'
                                    formikProps={formikProps}
                                    label={Tr('address')}
                                    limit={200}
                                    required
                                />
                                <TextInput
                                    id='postCode'
                                    formikProps={formikProps}
                                    label={Tr('zip_code')}
                                    limit={6}
                                    required
                                />
                                <TextInput
                                    id='phone'
                                    formikProps={formikProps}
                                    label={Tr('phone_number')}
                                    limit={20}
                                />
                                <TextInput
                                    id='fax'
                                    formikProps={formikProps}
                                    label={Tr('fax')}
                                    limit={20}
                                />
                                <TextInput
                                    id='email'
                                    formikProps={formikProps}
                                    label={Tr('email')}
                                />
                                <TextInput
                                    id='nip'
                                    formikProps={formikProps}
                                    label={Tr('nip')}
                                    limit={10}
                                />
                                <TextInput
                                    id='regon'
                                    formikProps={formikProps}
                                    label={Tr('regon')}
                                    limit={14}
                                />

                                {!!wrapper.representativesTitleDictionary && !!wrapper.representativesRoleDictionary && <FieldArray
                                    name="representatives"
                                    render={(arrayHelpers) => (<>
                                        <BoldTypography>{Tr("representatives")}*</BoldTypography>

                                        <FormHelperText error>{getObjectValue("fieldsCount", formikProps.errors, null)}</FormHelperText>

                                        {
                                            getObjectValue("representatives", formikProps.values, [])
                                                .map((x, i) => (<OrganizationUnitRepresentative
                                                    key={i}
                                                    formikProps={formikProps}
                                                    index={i}
                                                    representativesRoleDictionary={wrapper.representativesRoleDictionary}
                                                    representativesTitleDictionary={wrapper.representativesTitleDictionary}
                                                    removeRepresentative={() => removeRepresentative(arrayHelpers, i)}
                                                />))
                                        }
                                        <GridBlock>
                                            <Button Icon={PlusOneIcon} variant="outlined" onClick={() => addRepresentative(arrayHelpers)}>
                                                {Tr('add_representative')}
                                            </Button>
                                        </GridBlock>
                                    </>)}
                                />
                                }


                            </GridBlock>
                        )}
                    </Form>
            }
        </Dialog>
    )
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(UserRegistrationAddOrganizationUnit);

function OrganizationUnitRepresentative(
    {
        formikProps,
        index,
        representativesTitleDictionary,
        representativesRoleDictionary,
        removeRepresentative

    }) {
    const classes = useStyle();

    return <>
        <GridBlock className={classes.representativeGrid}>
            <Select
                formikProps={formikProps}
                id={`representatives.${index}.title`}
                label={Tr("representative_title")}
                items={Object.keys(representativesTitleDictionary).map(x => createSelectItem(x, representativesTitleDictionary[x]))}
                required
            />
            <TextInput
                formikProps={formikProps}
                id={`representatives.${index}.name`}
                label={Tr("representative_name")}
                required
            />
            <Select
                formikProps={formikProps}
                id={`representatives.${index}.role`}
                label={Tr("representative_role")}
                items={Object.keys(representativesRoleDictionary).map(x => createSelectItem(x, representativesRoleDictionary[x]))}
                required
            />
            <WorkableIconDeleteButton onClick={removeRepresentative}/>
        </GridBlock>

    </>

}